import { roleAtLeast, type PromptView } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { createPrompt, fetchPrompts } from '../api/agents.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatRelative, plural } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

export function PromptsPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const canEdit = roleAtLeast(workspace.role, 'admin')
  const [showForm, setShowForm] = useState(false)

  const prompts = useQuery({
    queryKey: ['prompts', workspace.id],
    queryFn: () => fetchPrompts(workspace.id),
  })

  const create = useMutation({
    mutationFn: (input: { name: string; description: string; body: string }) =>
      createPrompt(workspace.id, {
        name: input.name,
        description: input.description,
        tags: [],
        body: input.body,
        note: 'первая версия',
        publish: true,
      }),
    onSuccess: (prompt) => {
      void queryClient.invalidateQueries({ queryKey: ['prompts', workspace.id] })
      setShowForm(false)
      void navigate(`/w/${workspace.id}/prompts/${prompt.id}`)
    },
  })

  const columns: Column<PromptView>[] = [
    {
      key: 'name',
      header: 'Промпт',
      render: (row) => (
        <div>
          <Link
            to={`/w/${workspace.id}/prompts/${row.id}`}
            className="font-medium text-accent hover:underline"
          >
            {row.name}
          </Link>
          {row.description && <p className="mt-0.5 text-xs text-muted">{row.description}</p>}
        </div>
      ),
    },
    {
      key: 'published',
      header: 'Опубликована',
      render: (row) =>
        row.publishedVersion ? (
          <Badge tone="ok">версия {row.publishedVersion.version}</Badge>
        ) : (
          /**
           * Промпт без опубликованной версии ничего не делает. Это не
           * ошибка, но и не нейтральное состояние: человек должен видеть,
           * что текст написан, а в работу не пущен.
           */
          <Badge tone="warn">ни одна не опубликована</Badge>
        ),
    },
    {
      key: 'versions',
      header: 'Версий',
      align: 'right',
      render: (row) => (
        <span className="text-ink-soft">
          {row.versionCount}
          {row.publishedVersion && row.publishedVersion.version < row.latestVersion && (
            <span className="ml-2 text-xs text-copper">
              последняя — {row.latestVersion}, не опубликована
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Изменён',
      align: 'right',
      render: (row) => <span className="text-muted">{formatRelative(row.updatedAt)}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        title="Промпты"
        description="Текст инструкции — это код агента, поэтому у него версии, сравнение и откат. Правка создаёт следующую версию, публикация переставляет указатель на неё."
        actions={
          canEdit && (
            <Button variant="primary" onClick={() => setShowForm((value) => !value)}>
              {showForm ? 'Свернуть' : 'Новый промпт'}
            </Button>
          )
        }
      />

      {showForm && canEdit && (
        <Panel title="Новый промпт" className="mb-5">
          <PromptForm
            loading={create.isPending}
            error={create.error}
            onSubmit={(input) => create.mutate(input)}
          />
        </Panel>
      )}

      {prompts.error && <ErrorPanel error={prompts.error} onRetry={() => void prompts.refetch()} />}

      <DataTable
        rows={prompts.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        isLoading={prompts.isLoading}
        isRefreshing={prompts.isFetching && !prompts.isLoading}
        empty={
          <EmptyState
            title="Промптов пока нет"
            description={
              canEdit
                ? 'Начните с системной инструкции: это единственный промпт, без которого агента нельзя опубликовать.'
                : 'Создавать промпты может администратор воркспейса.'
            }
          />
        }
      />

      {prompts.data && prompts.data.length > 0 && (
        <p className="mt-3 text-xs text-muted">
          {prompts.data.length} {plural(prompts.data.length, 'промпт', 'промпта', 'промптов')}
        </p>
      )}
    </>
  )
}

function PromptForm({
  loading,
  error,
  onSubmit,
}: {
  loading: boolean
  error: unknown
  onSubmit: (input: { name: string; description: string; body: string }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState('')

  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit({ name: name.trim(), description: description.trim(), body })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Название"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={120}
          placeholder="Системная инструкция"
          hint="Уникально внутри воркспейса"
        />
        <Field
          label="Пояснение"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={500}
          placeholder="Для чего этот промпт"
        />
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink-soft">Текст</span>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          required
          rows={10}
          className="w-full rounded-md border border-line bg-raised px-3 py-2 font-mono text-sm text-ink"
          placeholder="Ты продавец…"
        />
      </label>

      {error != null && <ErrorPanel error={error} />}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" loading={loading}>
          Создать и опубликовать
        </Button>
        <span className="text-xs text-muted">
          Версия 1 публикуется сразу — промпт без опубликованной версии ничего не делает
        </span>
      </div>
    </form>
  )
}
